import { useMemo, useCallback, ChangeEvent } from 'react';
import useInputState from '../../hooks/useInputState';
import useToggle from '../../hooks/useToggle';
import { IItemBase, ISwitchOptions, ItemId } from './types';

interface UseItemPanelOptions<TItemId, TItem> {
  onClose?: () => void;
  onSelect: (id: TItemId) => void;
  retainOnClose?: boolean;
  switchOptions?: ISwitchOptions<TItem>;
  allItems: TItem[];
  selectedOnTop?: boolean;
  selectedId?: TItemId;
  onSearch?: (searchTerm: string) => void;
}

interface UseItemPanelResult<TItemId, TItem> {
  query: string;
  activeSwitch: boolean;
  selectStore: (id: TItemId) => void;
  filteredItems: TItem[] | undefined;
  selected: TItem | undefined;
  handleQueryChange: (e: ChangeEvent<HTMLInputElement>) => void;
  toggleSwitch: () => void;
}

const useItemPanel = <TItemId extends ItemId, TItem extends IItemBase<TItemId>>(
  options: UseItemPanelOptions<TItemId, TItem>,
): UseItemPanelResult<TItemId, TItem> => {
  const {
    onClose,
    onSelect,
    retainOnClose,
    switchOptions,
    allItems,
    selectedOnTop,
    selectedId,
    onSearch,
  } = options;

  const [query, onQueryChange] = useInputState('');
  const [activeSwitch, toggleSwitch] = useToggle(switchOptions?.initialSwitchState);

  const selectStore = useCallback(
    (id: TItemId) => {
      if (!retainOnClose && !!onClose) {
        onClose();
      }
      onSelect(id);
    },
    [onClose, onSelect, retainOnClose],
  );

  const filteredItems = useMemo(
    () => (activeSwitch ? switchOptions?.handleSwitch(allItems) : allItems),
    [activeSwitch, switchOptions, allItems],
  );

  const selected = useMemo(
    () =>
      selectedOnTop && filteredItems && !!selectedId
        ? filteredItems.find(({ id }) => id === selectedId)
        : undefined,
    [selectedOnTop, filteredItems, selectedId],
  );

  const handleQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onQueryChange(e);
      if (onSearch) {
        onSearch(e.target.value);
      }
    },
    [onQueryChange, onSearch],
  );

  return {
    query,
    activeSwitch,
    selectStore,
    filteredItems,
    selected,
    handleQueryChange,
    toggleSwitch,
  };
};

export default useItemPanel;
